// Step 7 Phase 6 — adaptive variant resolution for HD/SD multi-variant delivery.
//
// Pure function: given a manifest + device class + optional user override,
// pick the best variant to load. No I/O, no side effects.
//
// Algorithm:
//   1. If override is provided AND that variant is ready, use it (user choice wins).
//   2. Otherwise compute preferred = deviceClass === 'mobile' ? 'sd' : 'hd'.
//   3. If preferred ready, use it.
//   4. Otherwise fall back to the other (sd ↔ hd). If that's ready, return with
//      a warning so the UI can hint the fallback was applied.
//   5. Final fallback: 'source' (legacy ply). Always returns a warning.
//   6. If no variant ready at all → return null. Caller falls back to legacy
//      `/api/model/:id` (mkkellogg adapter via Spark — both compatible).
//
// Splat count budget (parent plan §Fase 6): mobile preferred SD due to
// WebKit memory budget. If the SD variant has splat_count > MOBILE_BUDGET
// the resolver still picks SD (best available) but tags the warning so the
// viewer can show a soft warning. Hard reject is NOT done here — the user
// may still want to attempt the load.

import type { ModelManifest, ModelManifestVariant } from '../api/types.js';
import type { DeviceClass } from '../lib/device.js';

// WS1 perf governor — budget di splat ATTIVI per classe device, allineati alle
// linee guida di produzione (Spark/PlayCanvas: ~1M mobile / ~3M desktop). Soft:
// usati per il warning, non per gate hard. Diventano la leva di selezione reale
// quando il worker produrrà varianti a conteggio differenziato (WS2).
const MOBILE_SPLAT_BUDGET = 1_000_000;
const DESKTOP_SPLAT_BUDGET = 3_000_000;

// Punto 2 SOG full SH 2026-06-08 (Codex iter1 HIGH #1 fix): override esteso a
// `rad` e `source` per visual regression / debugging deterministic. Per default
// users (UI variant switcher) restano solo `hd`/`sd` quotidiani; `rad`/`source`
// arrivano via URL `?variant=` parser (src/url/parser.ts:readVariantParam).
export type VariantOverride = 'hd' | 'sd' | 'rad' | 'source' | null;

export interface ResolvedVariant {
  variant: ModelManifestVariant;
  // Warnings the UI may surface to the user. Empty string when
  // the preferred variant for the device was used and within budget.
  warning:
    | ''
    | 'fallback-source'
    | 'fallback-hd-on-mobile'
    | 'fallback-sd-on-desktop'
    | 'over-budget'
    | 'override-not-ready';
}

function findReady(
  manifest: ModelManifest,
  variantName: 'hd' | 'sd' | 'source' | 'rad',
): ModelManifestVariant | null {
  return (
    manifest.available_variants.find((v) => v.variant === variantName && v.status === 'ready') ??
    null
  );
}

function isOverBudget(variant: ModelManifestVariant, deviceClass: DeviceClass): boolean {
  if (variant.splat_count === null) return false; // unknown → trust caller
  const budget = deviceClass === 'mobile' ? MOBILE_SPLAT_BUDGET : DESKTOP_SPLAT_BUDGET;
  return variant.splat_count > budget;
}

export function selectVariantForDevice(
  manifest: ModelManifest,
  deviceClass: DeviceClass,
  override: VariantOverride,
): ResolvedVariant | null {
  // 1. Honor explicit user override when ready.
  if (override !== null) {
    const overrideVariant = findReady(manifest, override);
    if (overrideVariant !== null) {
      return {
        variant: overrideVariant,
        warning: isOverBudget(overrideVariant, deviceClass) ? 'over-budget' : '',
      };
    }
    // Override not ready: fall through to auto-selection but tag the warning.
    const auto = autoSelect(manifest, deviceClass);
    if (auto !== null) {
      return { variant: auto.variant, warning: 'override-not-ready' };
    }
    return null;
  }
  return autoSelect(manifest, deviceClass);
}

/**
 * Punto 2 SOG full SH 2026-06-08 (Codex Option C): SOG `sd` con full SH ha
 * priorita' sopra `.rad` SOLO se:
 *   - variant 'sd' ready
 *   - sog_has_full_sh === true (strict ===: NULL/false/missing → fail-safe legacy)
 *   - sog_autoselect_enabled !== false (kill switch D1 no-redeploy)
 *   - splat_count <= device budget (MOBILE_SPLAT_BUDGET | DESKTOP_SPLAT_BUDGET)
 *
 * Razionale Codex: `.sog` full SH e' NON LoD (tutti gli splat in GPU mem). Per
 * scene Roma 4vb 16M splat: mobile/desktop continuano a usare `.rad`
 * (over-budget → preserva LIVE prod stability post-backfill). Per scene future
 * <= budget: prefer SOG → Corona materials realistic (vetro, marmo, metalli)
 * abilitati per HEAVY tier €25k/anno.
 *
 * `splat_count === null` (unknown) NON e' over-budget by isOverBudget contract
 * MA preferiamo conservativi: senza un count non possiamo garantire budget
 * → SKIP prefer-SOG (Codex blind_spot: "Splat count is an imperfect memory proxy").
 */
function prefersSogFullSh(
  manifest: ModelManifest,
  deviceClass: DeviceClass,
): ModelManifestVariant | null {
  const sd = findReady(manifest, 'sd');
  if (sd === null) return null;
  // Codex iter1 LOW fix: enforce format='sog' esplicit. Lo schema permette `sd`
  // con format ply/compressed.ply/ksplat per backward-compat path legacy: NON
  // possiamo prefer-SOG su una variante che non e' effettivamente SOG.
  if (sd.format !== 'sog') return null;
  if (sd.sog_has_full_sh !== true) return null;
  // Kill switch: explicit false → opt-out. Default backend NOT NULL DEFAULT 1 →
  // mai undefined runtime, ma defensive accept anche undefined come "enabled".
  if (sd.sog_autoselect_enabled === false) return null;
  // splat_count required: senza conta non possiamo gate il device budget.
  if (sd.splat_count === null) return null;
  if (isOverBudget(sd, deviceClass)) return null;
  return sd;
}

function autoSelect(manifest: ModelManifest, deviceClass: DeviceClass): ResolvedVariant | null {
  // Punto 2 (Codex Option C): prefer SOG full SH PRIMA di `.rad` quando sicuro
  // (capability flag true + kill switch on + budget OK). Vedi prefersSogFullSh.
  const sogPreferred = prefersSogFullSh(manifest, deviceClass);
  if (sogPreferred !== null) {
    return { variant: sogPreferred, warning: '' };
  }

  // WS4: preferisci `rad` (LOD-tree streamabile) quando pronto. Si adatta a ogni
  // device via SparkRenderer.lodSplatCount (budget splat attivi), quindi NON e' mai
  // over-budget e non richiede la scelta hd/sd. hd/sd/source restano fallback.
  const radVariant = findReady(manifest, 'rad');
  if (radVariant !== null) {
    return { variant: radVariant, warning: '' };
  }

  const preferred = deviceClass === 'mobile' ? 'sd' : 'hd';
  const alternative = preferred === 'sd' ? 'hd' : 'sd';

  const preferredVariant = findReady(manifest, preferred);
  if (preferredVariant !== null) {
    return {
      variant: preferredVariant,
      warning: isOverBudget(preferredVariant, deviceClass) ? 'over-budget' : '',
    };
  }

  const altVariant = findReady(manifest, alternative);
  if (altVariant !== null) {
    const warning = deviceClass === 'mobile' ? 'fallback-hd-on-mobile' : 'fallback-sd-on-desktop';
    return { variant: altVariant, warning };
  }

  const sourceVariant = findReady(manifest, 'source');
  if (sourceVariant !== null) {
    return { variant: sourceVariant, warning: 'fallback-source' };
  }

  return null;
}

export const _testing = {
  MOBILE_SPLAT_BUDGET,
  DESKTOP_SPLAT_BUDGET,
};
